import type { ActionTree } from "vuex";
import { languageKeys, setApplicationLocale, t } from "@/language/translation";
import {
  activateDynamicTranslationCatalog,
  activateEmptyDynamicTranslationCatalog,
  loadDynamicTranslationCatalog,
} from "@/language/dynamicTranslations";
import { changeStaticTranslationLanguage } from "@/language/staticTranslations";

const localState = {};
let latestLocaleRequest = 0;

function checkIfLanguageIsSupported(locale: string) {
  const localeElements = locale.split("-");
  if (localeElements.length === 1) {
    if (languageKeys.some((lang) => lang === localeElements[0])) {
      return localeElements[0];
    }
  } else if (localeElements.length > 1) {
    const specificLanguage = `${localeElements[0]}_${localeElements[1]}`;
    if (languageKeys.some((lang) => lang === specificLanguage)) {
      return specificLanguage;
    }
    if (languageKeys.some((lang) => lang === localeElements[0])) {
      return localeElements[0];
    }
  }
  return null;
}

function reportCatalogLoadFailure(error: unknown) {
  // eslint-disable-next-line no-console
  console.error("Failed to load the dynamic translation catalog", error);
  if (window.$toast == null) return;
  window.$toast({
    severity: "error",
    summary: t("api.toast.error.title"),
    detail: t("api.toast.error.networkError"),
  });
}

const actions: ActionTree<typeof localState, unknown> = {
  async setBrowserLocal(context) {
    for (let i = 0; i < navigator.languages.length; i += 1) {
      const matchingLanguage = checkIfLanguageIsSupported(navigator.languages[i]);
      if (matchingLanguage != null) {
        await context.dispatch("setLocale", matchingLanguage);
        return;
      }
    }
    await context.dispatch("setLocale", languageKeys[0]);
  },
  async setLocale(context, requestedLocale: (typeof languageKeys)[number]) {
    if (requestedLocale == null) return false;

    const requestId = ++latestLocaleRequest;
    const ISOCompatibleLocale = requestedLocale.replace("_", "-");
    const GraphQLCompatibleLocale = requestedLocale.replace("-", "_");
    let catalog: Awaited<ReturnType<typeof loadDynamicTranslationCatalog>> | undefined;

    if (context.rootGetters["login/isLogin"]) {
      try {
        catalog = await loadDynamicTranslationCatalog(ISOCompatibleLocale);
      } catch (error) {
        reportCatalogLoadFailure(error);
      }
    }
    if (requestId !== latestLocaleRequest) return false;

    await changeStaticTranslationLanguage(ISOCompatibleLocale);
    if (requestId !== latestLocaleRequest) return false;

    if (catalog != null) activateDynamicTranslationCatalog(catalog);
    else activateEmptyDynamicTranslationCatalog(ISOCompatibleLocale);
    setApplicationLocale(ISOCompatibleLocale);
    await context.dispatch("clientCache/setLastLocale", ISOCompatibleLocale, { root: true });
    context.commit("login/setLanguage", GraphQLCompatibleLocale, { root: true });
    return true;
  },
};

export default {
  namespaced: true,
  state: localState,
  actions,
};
